import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import RichTextEditor from "../../components/RichTextEditor";
import { MediaAttachments } from "../../components/MediaAttachments";

const DEFAULT = {
  type: "mcq",
  text: "",
  options: ["", "", "", ""],
  correct_answers: [],
  marks: 1,
  negative_marks: 0,
  difficulty: "medium",
  subject_id: "",
  topic: "",
  explanation: "",
  language: "python",
  starter_code: "",
  test_cases: [],
  attachments: [],
  option_attachments: {},
};

export default function QuestionForm() {
  const { qid } = useParams();
  const editing = qid && qid !== "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT);
  const [subjects, setSubjects] = useState([]);
  const [codingGate, setCodingGate] = useState(null);

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data));
    api.get("/coding/gate-status").then((r) => setCodingGate(r.data)).catch(() => {});
    if (editing) {
      api.get("/questions", { params: { search: "" } }).then((r) => {
        const q = r.data.find((x) => x.question_id === qid);
        if (q) setForm(q);
      });
    }
  }, [editing, qid]);

  const setType = (type) => {
    let options = form.options;
    let correct = [];
    if (type === "true_false") options = ["True", "False"];
    else if (type === "short_answer" || type === "coding") options = [];
    else if (form.options.length === 0) options = ["", "", "", ""];
    setForm({ ...form, type, options, correct_answers: correct });
  };

  const toggleCorrect = (idx) => {
    const s = String(idx);
    if (form.type === "msq") {
      setForm({
        ...form,
        correct_answers: form.correct_answers.includes(s)
          ? form.correct_answers.filter((v) => v !== s)
          : [...form.correct_answers, s],
      });
    } else {
      setForm({ ...form, correct_answers: [s] });
    }
  };

  const submit = async () => {
    // Strip HTML tags for emptiness check
    const plainText = (form.text || "").replace(/<[^>]+>/g, "").trim();
    if (!plainText) { toast.error("Question text required"); return; }
    if (form.type === "coding") {
      if (!form.test_cases || form.test_cases.length === 0) { toast.error("Add at least one test case"); return; }
    } else if (form.type !== "short_answer" && form.correct_answers.length === 0) {
      toast.error("Mark at least one correct option"); return;
    } else if (form.type === "short_answer" && form.correct_answers.length === 0) {
      toast.error("Add at least one accepted answer"); return;
    }
    try {
      const payload = { ...form, marks: Number(form.marks), negative_marks: Number(form.negative_marks) };
      if (editing) await api.put(`/questions/${qid}`, payload);
      else await api.post("/questions", payload);
      toast.success("Saved");
      navigate("/app/questions");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div>
      <PageHeader overline={editing ? "Edit" : "New"} title={editing ? "Edit question" : "Create question"} />

      <div className="grid lg:grid-cols-3 gap-0 border border-zinc-200">
        <div className="lg:col-span-2 p-8 border-r border-zinc-200 space-y-6">
          <Field label="Question Text">
            <RichTextEditor
              value={form.text}
              onChange={(html) => setForm({ ...form, text: html })}
              placeholder="Write your question. Use ∑x button for LaTeX math."
              testId="q-text-editor"
            />
          </Field>

          <Field label="Question Media (images, videos, PDFs, audio)">
            <MediaAttachments
              value={form.attachments || []}
              onChange={(next) => setForm({ ...form, attachments: next })}
              label={null}
              testIdPrefix="q-media"
            />
          </Field>

          <Field label="Type">
            <div className="flex flex-wrap gap-2">
              {[
                ["mcq", "Multiple Choice"],
                ["msq", "Multiple Select"],
                ["true_false", "True / False"],
                ["short_answer", "Short Answer"],
                ["coding", "Coding"],
              ].map(([v, l]) => {
                const disabled = v === "coding" && codingGate && !codingGate.coding_allowed;
                return (
                  <button
                    key={v}
                    type="button"
                    disabled={disabled}
                    title={disabled ? "Coding disabled — Judge0 not connected" : ""}
                    onClick={() => setType(v)}
                    className={`px-3 py-2 text-xs font-medium border ${form.type === v ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:bg-zinc-50"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid={`q-type-${v}`}
                  >{l}</button>
                );
              })}
            </div>
          </Field>

          {form.type === "coding" && (
            <>
              <Field label="Language">
                <select
                  value={form.language || "python"}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
                  data-testid="q-coding-lang"
                >
                  <option value="python">Python</option>
                  <option value="javascript">JavaScript</option>
                  <option value="c">C</option>
                  <option value="cpp">C++</option>
                </select>
              </Field>
              <Field label="Starter Code">
                <textarea
                  rows={6}
                  value={form.starter_code || ""}
                  onChange={(e) => setForm({ ...form, starter_code: e.target.value })}
                  className="w-full font-mono text-sm bg-zinc-950 text-zinc-100 p-3 focus:outline-none"
                  spellCheck={false}
                  data-testid="q-starter-code"
                />
              </Field>
              <Field label="Test Cases">
                <div className="space-y-2">
                  {(form.test_cases || []).map((tc, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start border border-zinc-200 p-3">
                      <div className="col-span-5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">stdin</div>
                        <textarea
                          rows={2}
                          value={tc.stdin || ""}
                          onChange={(e) => {
                            const tcs = [...form.test_cases]; tcs[i] = { ...tcs[i], stdin: e.target.value };
                            setForm({ ...form, test_cases: tcs });
                          }}
                          className="w-full font-mono text-xs border border-zinc-300 p-2"
                          data-testid={`tc-stdin-${i}`}
                        />
                      </div>
                      <div className="col-span-5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">expected output</div>
                        <textarea
                          rows={2}
                          value={tc.expected_output || ""}
                          onChange={(e) => {
                            const tcs = [...form.test_cases]; tcs[i] = { ...tcs[i], expected_output: e.target.value };
                            setForm({ ...form, test_cases: tcs });
                          }}
                          className="w-full font-mono text-xs border border-zinc-300 p-2"
                          data-testid={`tc-expected-${i}`}
                        />
                      </div>
                      <div className="col-span-2 flex flex-col gap-2">
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={!!tc.hidden}
                            onChange={(e) => {
                              const tcs = [...form.test_cases]; tcs[i] = { ...tcs[i], hidden: e.target.checked };
                              setForm({ ...form, test_cases: tcs });
                            }}
                          />
                          Hidden
                        </label>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, test_cases: form.test_cases.filter((_, idx) => idx !== i) })}
                          className="text-xs text-red-600 hover:underline"
                        >Remove</button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, test_cases: [...(form.test_cases || []), { stdin: "", expected_output: "", hidden: false }] })}
                    className="text-xs border border-dashed border-zinc-300 px-3 py-2 hover:bg-zinc-50 inline-flex items-center gap-1"
                    data-testid="add-test-case-btn"
                  ><Plus size={12} /> Add test case</button>
                </div>
              </Field>
            </>
          )}

          {form.type !== "short_answer" && form.type !== "coding" ? (
            <Field label="Options (toggle correct)">
              <div className="space-y-2">
                {form.options.map((opt, i) => (
                  <div key={i} className="border border-zinc-200 p-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggleCorrect(i)}
                        className={`min-w-10 h-10 flex items-center justify-center text-xs font-bold border ${form.correct_answers.includes(String(i)) ? "bg-green-600 text-white border-green-700" : "border-zinc-300"}`}
                        data-testid={`q-correct-${i}`}
                      >
                        {String.fromCharCode(65 + i)}
                      </button>
                      <input
                        value={opt}
                        onChange={(e) => {
                          const opts = [...form.options];
                          opts[i] = e.target.value;
                          setForm({ ...form, options: opts });
                        }}
                        placeholder={`Option ${i + 1}`}
                        className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                        data-testid={`q-option-${i}`}
                        disabled={form.type === "true_false"}
                      />
                      {form.type !== "true_false" && form.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            const opts = form.options.filter((_, idx) => idx !== i);
                            const oa = { ...(form.option_attachments || {}) };
                            delete oa[String(i)];
                            setForm({ ...form, options: opts, correct_answers: [], option_attachments: oa });
                          }}
                          className="p-2 text-zinc-500 hover:text-red-600"
                        ><X size={16} /></button>
                      )}
                    </div>
                    {form.type !== "true_false" && (
                      <div className="pl-12">
                        <MediaAttachments
                          value={(form.option_attachments || {})[String(i)] || []}
                          onChange={(next) => setForm({ ...form, option_attachments: { ...(form.option_attachments || {}), [String(i)]: next } })}
                          label={null}
                          compact
                          testIdPrefix={`q-opt-${i}-media`}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {form.type !== "true_false" && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, options: [...form.options, ""] })}
                    className="text-xs border border-dashed border-zinc-300 px-3 py-2 hover:bg-zinc-50 inline-flex items-center gap-1"
                  ><Plus size={12} /> Add option</button>
                )}
              </div>
            </Field>
          ) : form.type === "short_answer" ? (
            <Field label="Accepted answers (comma separated)">
              <input
                value={form.correct_answers.join(", ")}
                onChange={(e) => setForm({ ...form, correct_answers: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="q-accepted-answers"
              />
            </Field>
          ) : null}

          <Field label="Explanation (optional)">
            <textarea
              rows={2}
              value={form.explanation}
              onChange={(e) => setForm({ ...form, explanation: e.target.value })}
              className="w-full border border-zinc-300 p-3 text-sm focus:border-zinc-900 focus:outline-none"
            />
          </Field>
        </div>

        <div className="p-8 space-y-6 bg-zinc-50">
          <Field label="Subject">
            <select
              value={form.subject_id || ""}
              onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="q-subject"
            >
              <option value="">— None —</option>
              {subjects.map((s) => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Topic">
            <input
              value={form.topic || ""}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="q-topic"
            />
          </Field>
          <Field label="Difficulty">
            <select
              value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
              data-testid="q-difficulty"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Marks">
              <input
                type="number"
                step="0.5"
                value={form.marks}
                onChange={(e) => setForm({ ...form, marks: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
                data-testid="q-marks"
              />
            </Field>
            <Field label="Negative">
              <input
                type="number"
                step="0.25"
                value={form.negative_marks}
                onChange={(e) => setForm({ ...form, negative_marks: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
                data-testid="q-neg-marks"
              />
            </Field>
          </div>

          <button
            onClick={submit}
            className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800"
            data-testid="q-save-btn"
          >
            {editing ? "Update question" : "Create question"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">{label}</div>
      {children}
    </div>
  );
}
